import { useCallback, useEffect, useState, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import Moment from 'moment';
import Deployment from '../../services/deployment';
import PubSub from '../../services/PubSub';
import Git from '../../services/git';

import { useHasPermission } from '../../common/Hooks';
import { useTranslation } from 'react-i18next';
import Product from '../../services/product';
import Utils from '../../utils/utils';
import MigrateDeploymentSettings from './MigrateDeploymentSettings';
import UploadArea from '../controls/UploadArea';
import OverrideHistoryModal from './OverrideHistoryModal';
import ACTION_TYPES from '../../config/actionTypes.js';
import GenericDropdown from '../controls/GenericDropdown';
import Toolbar from '../controls/Toolbar.jsx';

const DATE_MOMENT_FORMAT = "DD-MMM-yyyy HH:mm";

const DeploymentManagement = () => {
    const { id } = useParams();
    const [translate] = useTranslation();

    const [settingsSetId, setSettingsSetId] = useState('');
    const [products, setProducts] = useState({});
    const [deployment, setDeployment] = useState({});
    const [productList, setProductList] = useState([]);
    const [selectedProductVersions, setSelectedProductVersions] = useState([]);
    const [actionInProgress, setActionInProgress] = useState(ACTION_TYPES.AVAILABLE);
    const [enableOverrideLogoIcon, setEnableOverrideLogoIcon] = useState(false);
    const [showOverrideModal, setShowOverrideModal] = useState(false);
    const [showMigrateDeploymentSettingsModal, setShowMigrateDeploymentSettingsModal] = useState(false);

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: deployment.environment ? deployment.environment._id : null,
        deployment: id,
        type: 'deployment'
    });
    const productPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: 'domain'
    });

    const allowedUpdate = useMemo(() => {
        return permissions.update && actionInProgress === ACTION_TYPES.AVAILABLE;
    }, [permissions.update, actionInProgress]);

    const checkDeployStatus = useCallback((deploymentId) => {
        Deployment.getDeployment(deploymentId, false)
            .then((response) => {
                const retrievedDeployment = response.data;

                if (retrievedDeployment.actionInProgress !== ACTION_TYPES.DEPLOYMENT_IN_PROGRESS) {
                    PubSub.publish('timer-stop', deploymentId);
                }

                if (retrievedDeployment.otmApp.deployMessages.length <= 0) {
                    return;
                }

                const message = retrievedDeployment.otmApp.deployMessages.findLast((el) => el.displayMessage === true);
                if (message) {
                    PubSub.publish('show_deployment_info', deploymentId, message.message);
                }
            });
    }, []);

    const checkDownloadStatus = useCallback((deploymentId) => {
        Deployment.getDeployment(deploymentId, false)
            .then((response) => {
                const retrievedDeployment = response.data;

                if (retrievedDeployment.actionInProgress !== ACTION_TYPES.DOWNLOAD_IN_PROGRESS) {
                    PubSub.publish('timer-stop', deploymentId);

                    if (retrievedDeployment.downloadUrl) {
                        window.open(retrievedDeployment.downloadUrl, '_self');
                    }
                }

                if (retrievedDeployment.otmApp.downloadMessages.length <= 0) {
                    return;
                }

                const message = retrievedDeployment.otmApp.downloadMessages.findLast((el) => el.displayMessage === true);
                if (message) {
                    PubSub.publish('show_deployment_info', deploymentId, message.message, translate('OTM_DOWNLOAD_STATUS'));
                }
            });
    }, [translate]);

    const checkBuildOnPremStatus = useCallback((deploymentId) => {
        Deployment.getDeployment(deploymentId, false)
            .then((response) => {
                const retrievedDeployment = response.data;
                if (retrievedDeployment.actionInProgress !== ACTION_TYPES.BUILD_ONPREM_IN_PROGRESS) {
                    PubSub.publish('timer-stop', deploymentId);
                }
                if (retrievedDeployment.otmApp.buildOnPremMessages.length <= 0) {
                    return;
                }
                const message = retrievedDeployment.otmApp.buildOnPremMessages.findLast((el) => el.displayMessage === true);
                if (message) {
                    PubSub.publish('show_deployment_info', deploymentId, message.message, translate('OTM_BUILD_STATUS'));
                }
            });
    }, [translate]);

    const checkDownloadOnPremBuildStatus = useCallback((deploymentId) => {
        Deployment.getDeployment(deploymentId, false)
            .then((response) => {
                const retrievedDeployment = response.data;
                if (retrievedDeployment.actionInProgress !== ACTION_TYPES.DOWNLOAD_ONPREM_BUILD_IN_PROGRESS) {
                    PubSub.publish('timer-stop', deploymentId);

                    if (retrievedDeployment.downloadOnPremBuildUrl) {
                        window.open(retrievedDeployment.downloadOnPremBuildUrl, '_self');
                    }
                }
                if (retrievedDeployment.otmApp.downloadOnPremBuildMessages.length <= 0) {
                    return;
                }
                const message = retrievedDeployment.otmApp.downloadOnPremBuildMessages.findLast((el) => el.displayMessage === true);
                if (message) {
                    PubSub.publish('show_deployment_info', deploymentId, message.message, translate('OTM_DOWNLOAD_BUILD_STATUS'));
                }
            });
    }, [translate]);

    const getDeployment = useCallback(async (id) => {
        const response = await Deployment.getDeployment(id);
        if (!response.error) {
            setSettingsSetId(response.data.settingsSet);
            setDeployment(response.data);

            switch (response.data.actionInProgress) {
                case ACTION_TYPES.DEPLOYMENT_IN_PROGRESS: {
                    PubSub.publish('timer-start', id, checkDeployStatus);
                    setActionInProgress(ACTION_TYPES.DEPLOYMENT_IN_PROGRESS);
                    break;
                }
                case ACTION_TYPES.DOWNLOAD_IN_PROGRESS: {
                    PubSub.publish('timer-start', id, checkDownloadStatus);
                    setActionInProgress(ACTION_TYPES.DOWNLOAD_IN_PROGRESS);
                    break;
                }
                case ACTION_TYPES.BUILD_ONPREM_IN_PROGRESS: {
                    PubSub.publish('timer-start', id, checkBuildOnPremStatus);
                    setActionInProgress(ACTION_TYPES.BUILD_ONPREM_IN_PROGRESS);
                    break;
                }
                case ACTION_TYPES.DOWNLOAD_ONPREM_BUILD_IN_PROGRESS: {
                    PubSub.publish('timer-start', id, checkDownloadOnPremBuildStatus);
                    setActionInProgress(ACTION_TYPES.DOWNLOAD_ONPREM_BUILD_IN_PROGRESS);
                    break;
                }
                default: { setActionInProgress(ACTION_TYPES.AVAILABLE); }
            }

            const productResponse = await Product.getAllProductVersions(Utils.getDomain());

            if (!productResponse.error) {
                setProducts(productResponse.data);
                setProductList(Object.keys(productResponse.data));
                setSelectedProductVersions(productResponse.data[response.data.productVersion.name]);
                setEnableOverrideLogoIcon(response.data.productVersion.enableOverrideLogoIcon);
            }
        }
    }, [checkDownloadStatus, checkDeployStatus, checkBuildOnPremStatus, checkDownloadOnPremBuildStatus]);

    useEffect(() => {
        if (id) {
            getDeployment(id);
        }
        const subscriptionId = PubSub.subscribe('refresh-deployment', () => {
            getDeployment(id);
        });

        return () => PubSub.unsubscribe('refresh-deployment', subscriptionId);
    }, [id, getDeployment]);

    const selectProduct = useCallback((index) => {
        setSelectedProductVersions([...products[productList[index]]]);
    }, [products, productList, setSelectedProductVersions]);

    const updateDeployment = useCallback(async ({ updatedDeployment }) => {
        let requestBody;
        if (updatedDeployment) {
            requestBody = {
                productVersion: updatedDeployment.productVersion
            }
        }
        else {
            requestBody = {
                name: deployment.name,
                description: deployment.description,
                productVersion: deployment.productVersion
            };
        }

        requestBody.actionInProgress = deployment.actionInProgress;

        const response = await Deployment.updateDeployment(id, requestBody);
        if (!response.error) {
            PubSub.publish('refresh-environment-deployments', { deployment });
            await getDeployment(id);
        }
    }, [deployment, id, getDeployment]);

    const updateProductInDeployment = useCallback((id) => {
        setDeployment(deployment => {
            const updatedDeployment = { ...deployment };
            if (!updatedDeployment.productVersion) {
                updatedDeployment.productVersion = {};
            }
            updatedDeployment.productVersion._id = id;
            updatedDeployment.productVersion.settingsSet = selectedProductVersions.find(version => version._id === id).settingsSet;

            updateDeployment({ updatedDeployment });
            return updatedDeployment;
        });

    }, [selectedProductVersions, updateDeployment, setDeployment]);

    const handleDeploy = useCallback((deploymentId) => {
        Deployment.deploy(deploymentId)
            .then(() => {
                checkDeployStatus(deploymentId);
            });

        PubSub.publish('show_deployment_info', deploymentId, translate('OTM_STARTED_DEPLOYMENT'), translate('OTM_DEPLOYMENT_STATUS'))
        setActionInProgress(ACTION_TYPES.DEPLOYMENT_IN_PROGRESS);

        PubSub.publish('timer-start', deploymentId, checkDeployStatus);
    }, [checkDeployStatus, translate]);

    const handleDownload = useCallback(async (deploymentId) => {
        Deployment.downloadApp(deploymentId)
            .then(() => {
                checkDownloadStatus(deploymentId);
            });

        PubSub.publish('show_deployment_info', deploymentId, translate('OTM_STARTED_DOWNLOAD'), translate('OTM_DOWNLOAD_STATUS'));
        setActionInProgress(ACTION_TYPES.DOWNLOAD_IN_PROGRESS);

        PubSub.publish('timer-start', deploymentId, checkDownloadStatus);
    }, [checkDownloadStatus, translate]);

    const handleBuildOnPremDeployment = useCallback((deploymentId) => {
        Deployment.buildOnPremDeployment(deploymentId)
            .then(() => {
                checkBuildOnPremStatus(deploymentId);
            });
        PubSub.publish('show_deployment_info', deploymentId, translate('OTM_STARTED_BUILDING'), translate('OTM_BUILD_STATUS'))
        setActionInProgress(ACTION_TYPES.BUILD_ONPREM_IN_PROGRESS);

        PubSub.publish('timer-start', deploymentId, checkBuildOnPremStatus);
    }, [checkBuildOnPremStatus, translate]);

    const handleDownloadOnPremBuild = useCallback((deploymentId) => {
        Deployment.downloadOnPremBuild(deploymentId)
            .then(() => {
                checkDownloadOnPremBuildStatus(deploymentId);
            });
        PubSub.publish('show_deployment_info', deploymentId, translate('OTM_STARTED_DOWNLOADING_BUILD'), translate('OTM_DOWNLOAD_BUILD_STATUS'))
        setActionInProgress(ACTION_TYPES.DOWNLOAD_ONPREM_BUILD_IN_PROGRESS);

        PubSub.publish('timer-start', deploymentId, checkDownloadOnPremBuildStatus);
    }, [checkDownloadOnPremBuildStatus, translate]);

    const uploadResources = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_RESOURCES);
        const response = await Deployment.uploadToResourcesFolder(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const uploadLogo = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_LOGO);
        const response = await Deployment.uploadLogo(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const uploadFavicon = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_FAVICON);
        const response = await Deployment.uploadFavicon(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const handleDeleteFavicon = useCallback(async (deploymentId) => {
        setActionInProgress(ACTION_TYPES.DELETE_FAVICON);
        const response = await Deployment.deleteFavicon(deploymentId);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const handleDeleteLogo = useCallback(async (deploymentId) => {
        setActionInProgress(ACTION_TYPES.DELETE_LOGO);
        const response = await Deployment.deleteLogo(deploymentId);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const uploadCustomerConfig = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_CUSTOMER_CONFIG);
        const response = await Git.uploadCustomerConfig(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const formattedProductList = useMemo(() => {
        return productList.map((product) => {
            return {
                value: product,
            };
        });
    }, [productList]);

    const deploymentUrl = useCallback(() => {
        return `https://${deployment.cname ? deployment.cname : deployment.otmApp?.cloudfrontUrl}${deployment.homePage}`
    }, [deployment])

    const renderSectionOnPremDeployment = useMemo(() => {
        return (
            <div className='col-12 col-lg-6 mt-3'>
                <div className='card h-100'>
                    <div className='card-header border-bottom'>
                        <Toolbar
                            heading={translate('OTM_ON_PREM_DEPLOYMENT')}
                        />
                    </div>
                    <div className='card-body'>
                        <div className='row mt-n2 align-items-end'>
                            <div className='col-auto mt-2'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => setShowMigrateDeploymentSettingsModal(true)}>
                                    {translate('OTM_MIGRATE')}
                                </Button>
                            </div>
                            <div className='col-auto mt-2'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => handleBuildOnPremDeployment(deployment._id)}>
                                    {translate('OTM_BUILD')}
                                </Button>
                            </div>
                            <div className='col-auto mt-2'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => handleDownloadOnPremBuild(deployment._id)}>
                                    {translate('OTM_DOWNLOAD_BUILD')}
                                </Button>

                            </div>
                        </div>
                        <div className='mt-3'>
                            <p className='card-text'>Use the migrate button to copy settings and configuration from another deployment</p>
                            <p className='card-text'>Then download the compiled application ready for deployment on your servers</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }, [allowedUpdate, deployment, translate, handleDownloadOnPremBuild, handleBuildOnPremDeployment]);

    const renderSectionSaaSDeployments = useMemo(() => {
        return (
            <div className='col-12 col-lg-6 mt-3'>
                <div className='card h-100'>
                    <div className='card-header border-bottom'>
                        <Toolbar
                            heading={translate('OTM_PRODUCT_UPGRADE_AND_DEPLOYMENT')}
                        />
                    </div>
                    <div className='card-body'>
                        <div className='row align-items-end'>
                            <label className='col-auto h6'>{translate('OTM_DEPLOYMENT_URL')}</label>
                            <a className='col-auto h6 text-primary' rel='noreferrer' target='_blank' href={deploymentUrl()}>
                                {deploymentUrl()}
                                {deployment.otmApp?.cloudfrontId ? <></> : <label className='ms-1' style={{ color: 'red' }}> {translate('OTM_NOT_DEPLOYED')}</label>}
                            </a>
                        </div>
                        <div className='row align-items-end mt-2'>
                            <div className='col-auto mt-2'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => setShowMigrateDeploymentSettingsModal(true)}>
                                    {translate('OTM_MIGRATE')}
                                </Button>
                            </div>
                            <div className='col-auto mt-2'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => handleDeploy(deployment._id)}>
                                    {translate('OTM_DEPLOY')}
                                </Button>
                            </div>
                        </div>
                        <div className='mt-3'>
                            <p className='card-text'>Use the migrate button to copy settings and configuration from another deployment</p>
                            <p className='card-text'>Then make your application publically available by clicking the deploy button</p>
                            <p className='card-text'>Contact OnTheMove to change the Deployment URL</p>
                        </div>
                        <div className='row'>
                            <div className='col-12 mt-3'>
                                <label className='col-auto h6'>{translate('OTM_LATEST_DEPLOY_TIME')}</label>
                                <div>
                                    {deployment.otmApp?.lastDeployTime ? 
                                        Moment(deployment.otmApp.lastDeployTime).format(DATE_MOMENT_FORMAT) : 
                                        translate("OTM_NO_TIME_RECORDED")}
                                </div>    
                            </div>
                            <div className='col-12 col-lg-6 mt-3'>
                                <label className='col-auto h6'>{translate('OTM_DISTRIBUTION_ID')}</label>
                                <div>
                                    {deployment.otmApp?.cloudfrontId ?
                                        deployment.otmApp.cloudfrontId :
                                        translate("OTM_NO_DISTRIBUTION")}
                                </div>
                            </div>
                            <div className='col-12 col-lg-6 mt-3'>
                                <label className='col-auto h6'>{translate('OTM_LATEST_OTM_APP_VERSION')}</label>    
                                <div>
                                    {deployment.otmApp?.versionHistory.length <= 0 ?
                                        translate("OTM_NO_VERSIONS") :
                                        (deployment.otmApp?.versionHistory[deployment.otmApp?.versionHistory.length - 1].version ? 
                                            deployment.otmApp?.versionHistory[deployment.otmApp?.versionHistory.length - 1].version :
                                            translate("OTM_NO_VERSIONS")
                                        )}                              
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }, [allowedUpdate, deployment, deploymentUrl, translate, handleDeploy]);

    const renderSectionAppConfig = useMemo(() => {
        return (
            <div className='col-12 col-lg-6 mt-3'>
                <div className='card h-100'>
                    <div className='card-header border-bottom'>
                        <Toolbar
                            heading={translate('OTM_APPLICATION_CONFIGURATION')}
                        />
                    </div>
                    <div className='card-body'>
                        <div className='row mt-n3 align-items-end'>
                            <div className='col-12 mt-3'>
                                <GenericDropdown
                                    defaultValue={deployment.productVersion?.name || ''}
                                    options={formattedProductList}
                                    heading={translate('OTM_PRODUCT')}
                                    onSelect={selectProduct}
                                    grow={true}
                                    readOnly={true}
                                />
                            </div>
                            <div className='col-12 col-lg mt-3'>
                                <GenericDropdown
                                    defaultValue={deployment.productVersion?.version || ''}
                                    options={selectedProductVersions}
                                    valueKey='_id'
                                    displayValueKey='productVersion'
                                    heading={translate('OTM_VERSION')}
                                    onSelect={updateProductInDeployment}
                                    grow={true}
                                    readOnly={!allowedUpdate || !productPermissions.read}
                                />
                            </div>
                            <div className='col-auto mt-3'>
                                <Button
                                    disabled={!allowedUpdate}
                                    variant='primary'
                                    onClick={() => handleDownload(deployment._id)}>
                                    {translate('OTM_DOWNLOAD')}
                                </Button>
                            </div>
                            {deployment.productVersion && deployment.productVersion.releaseNotes && (
                                <div className="col-12 mt-3">
                                    <span className="h6">{translate("OTM_RELEASE_NOTES")}:<a className="ps-2" href={deployment.productVersion.releaseNotes} target="_blank" rel="noreferrer">{deployment.productVersion.releaseNotes}</a></span>
                                </div>
                            )}
                            <div className="col-12 mt-3">
                                <p className='card-text'>In development/sandbox deployments, select the product and version</p>
                                <p className='card-text'>Download configuration source files to make changes in your editor of choice</p>
                                <p className='card-text'>Upload the modified 'overrides' folder and any extra resources and deploy to publish changes</p>
                            </div>
                            <div className='col-12 col-sm-6 mt-3 align-self-start'>
                                <UploadArea
                                    heading={'OTM_UPLOAD_OVERRIDES'}
                                    dropText={"OTM_DROP_OVERRIDES_FILES"}
                                    dragText={"OTM_DRAG_OVERRIDES_FILES"}
                                    disabled={!allowedUpdate}
                                    parentId={deployment._id}
                                    uploadCallback={uploadCustomerConfig}
                                    responseCallback={getDeployment}
                                />
                                <div className='mt-2'>
                                    <Button
                                        disabled={!permissions.read}
                                        variant='primary'
                                        onClick={() => setShowOverrideModal(true)}>
                                        {translate('OTM_OVERRIDE_HISTORY')}
                                    </Button>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 mt-3 align-self-start">
                                <UploadArea
                                    heading={"OTM_UPLOAD_RESOURCES"}
                                    dropText={"OTM_DROP_RESOURCES_FILES"}
                                    dragText={"OTM_DRAG_RESOURCES_FILES"}
                                    disabled={!allowedUpdate}
                                    parentId={deployment._id}
                                    uploadCallback={uploadResources}
                                />
                            </div>
                            {
                                enableOverrideLogoIcon &&
                                <>
                                    <div className="col-12 col-sm-6 mt-3 align-self-start">
                                        <UploadArea
                                            heading={"OTM_UPLOAD_LOGO"}
                                            dropText={"OTM_DROP_LOGO"}
                                            dragText={"OTM_DRAG_LOGO"}
                                            disabled={!allowedUpdate}
                                            parentId={deployment._id}
                                            uploadCallback={uploadLogo}
                                        />
                                        <div className='mt-2'>
                                            <Button
                                                disabled={!allowedUpdate}
                                                variant='primary'
                                                onClick={() => handleDeleteLogo(deployment._id)}>
                                                {translate('OTM_DELETE')}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 mt-3 align-self-start">
                                        <UploadArea
                                            heading={"OTM_UPLOAD_FAVICON"}
                                            dropText={"OTM_DROP_FAVICON"}
                                            dragText={"OTM_DRAG_FAVICON"}
                                            disabled={!allowedUpdate}
                                            parentId={deployment._id}
                                            uploadCallback={uploadFavicon}
                                        />
                                        <div className='mt-2'>
                                            <Button
                                                disabled={!allowedUpdate}
                                                variant='primary'
                                                onClick={() => handleDeleteFavicon(deployment._id)}>
                                                {translate('OTM_DELETE')}
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div >
        )
    }, [allowedUpdate, deployment, selectedProductVersions, formattedProductList, productPermissions, enableOverrideLogoIcon, uploadCustomerConfig, uploadResources,
        translate, getDeployment, selectProduct, updateProductInDeployment, uploadLogo, handleDownload, handleDeleteLogo, uploadFavicon, handleDeleteFavicon, setShowOverrideModal, permissions]);

    const renderManagementCard = useMemo(() => {
        if (deployment.productVersion?.onPrem === true) {
            return renderSectionOnPremDeployment;
        }
        if (deployment.productVersion?.onPrem === false) {
            return renderSectionSaaSDeployments;
        }

        return <></>;
    }, [deployment.productVersion, renderSectionSaaSDeployments, renderSectionOnPremDeployment]);


    if (!permissions.read) {
        return (
            <div className='p-4'>
                {translate('OTM_NO_PERMISSION_DEPLOYMENT')}
            </div>
        );
    }

    return (
        <>
            {
                showOverrideModal &&
                <OverrideHistoryModal
                    deployment={deployment}
                    onCancel={() => setShowOverrideModal(false)}
                />
            }
            <MigrateDeploymentSettings
                show={showMigrateDeploymentSettingsModal}
                destinationDeploymentId={deployment._id}
                destinationSettingsSetId={settingsSetId}
                onCancel={() => setShowMigrateDeploymentSettingsModal(false)} />

            <div className='row'>
                {renderSectionAppConfig}

                {renderManagementCard}
            </div>
        </>
    );
};

export default DeploymentManagement;
